import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Buildings, Plus, CaretRight } from '@phosphor-icons/react';

const ADMINS = ['global_admin', 'school_admin', 'principal', 'registrar'];
const LETTERS = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D', 'F'];

export default function University() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ADMINS.includes(user?.role);
  const isStudent = user?.role === 'student';
  const { data: programs } = useQuery({ queryKey: ['uni-programs'], queryFn: () => api.get('/university/programs').then((r) => r.data) });
  const [activeProgram, setActiveProgram] = useState(null);
  const { data: courses } = useQuery({ queryKey: ['uni-courses', activeProgram], queryFn: () => api.get('/university/courses', { params: { program_id: activeProgram } }).then((r) => r.data), enabled: !!activeProgram });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data), enabled: isAdmin });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: isAdmin });
  const school = (tenants || []).find((t) => ['school', 'university', 'college'].includes(t.type));
  const [pOpen, setPOpen] = useState(false);
  const [pform, setPform] = useState({ name: '', degree: 'B.Sc', department: '', duration_years: 3 });
  const [cOpen, setCOpen] = useState(false);
  const [cform, setCform] = useState({ semester: 1, code: '', title: '', credits: 4, faculty: '', prerequisites: [] });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sform, setSform] = useState({ reg_open: '', reg_close: '', max_credits: 0 });
  const [transcriptFor, setTranscriptFor] = useState(isStudent ? 'me' : '');
  const myStudentId = useQuery({ queryKey: ['uni-me'], queryFn: () => api.get('/university/enrollments').then((r) => r.data[0]?.student_id || null), enabled: isStudent }).data;
  const tsid = isStudent ? myStudentId : transcriptFor;
  const { data: transcript } = useQuery({ queryKey: ['uni-transcript', tsid], queryFn: () => api.get(`/university/students/${tsid}/transcript`).then((r) => r.data), enabled: !!tsid && tsid !== 'me' });
  const { data: enrollments } = useQuery({ queryKey: ['uni-enrollments', activeProgram], queryFn: () => api.get('/university/enrollments').then((r) => r.data) });

  const inval = () => { qc.invalidateQueries({ queryKey: ['uni-programs'] }); qc.invalidateQueries({ queryKey: ['uni-courses'] }); qc.invalidateQueries({ queryKey: ['uni-enrollments'] }); qc.invalidateQueries({ queryKey: ['uni-transcript'] }); };
  const pMut = useMutation({
    mutationFn: () => api.post('/university/programs', { ...pform, tenant_id: user?.tenant_id || school?.id, duration_years: parseInt(pform.duration_years, 10) || 3 }),
    onSuccess: () => { toast.success('Program created'); setPOpen(false); setPform({ name: '', degree: 'B.Sc', department: '', duration_years: 3 }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const pDel = useMutation({ mutationFn: (id) => api.delete(`/university/programs/${id}`), onSuccess: () => { toast.success('Program deleted'); setActiveProgram(null); inval(); }, onError: (e) => toast.error(errMsg(e)) });
  const cMut = useMutation({
    mutationFn: () => api.post('/university/courses', { ...cform, program_id: activeProgram, semester: parseInt(cform.semester, 10) || 1, credits: parseInt(cform.credits, 10) || 4 }),
    onSuccess: () => { toast.success('Course added'); setCOpen(false); setCform({ semester: 1, code: '', title: '', credits: 4, faculty: '', prerequisites: [] }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const advanceMut = useMutation({
    mutationFn: () => api.post(`/university/programs/${activeProgram}/advance-semester`),
    onSuccess: (r) => {
      const d = r.data;
      toast.success(`Advanced ${d.advanced.length} · held ${d.held.length} · completed ${d.completed.length}${d.skipped_prereq.length ? ` · ${d.skipped_prereq.length} course(s) skipped (prereq)` : ''}`,
        { description: d.held.slice(0, 3).map((h) => `${h.student}: ${h.reason}`).join(' · ') || undefined });
      inval();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const settingsMut = useMutation({
    mutationFn: () => api.patch(`/university/programs/${activeProgram}`, { reg_open: sform.reg_open || '', reg_close: sform.reg_close || '', max_credits: parseInt(sform.max_credits, 10) || 0 }),
    onSuccess: () => { toast.success('Registration rules saved'); setSettingsOpen(false); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const cDel = useMutation({ mutationFn: (id) => api.delete(`/university/courses/${id}`), onSuccess: () => { toast.success('Course deleted'); inval(); }, onError: (e) => toast.error(errMsg(e)) });
  const enrollMut = useMutation({
    mutationFn: ({ student_id, course_id, override, override_reason }) => api.post('/university/enrollments', { student_id, course_id, override: !!override, override_reason: override_reason || '' }),
    onSuccess: () => { toast.success('Enrolled'); inval(); },
    onError: (e, v) => {
      const msg = errMsg(e);
      if (msg.includes('override possible') && isAdmin) {
        if (window.confirm(`${msg}\n\nOverride and enroll anyway?`)) {
          const reason = window.prompt('Override reason (audited):') || '';
          enrollMut.mutate({ ...v, override: true, override_reason: reason });
          return;
        }
      }
      toast.error(msg);
    },
  });
  const gradeMut = useMutation({
    mutationFn: ({ eid, letter }) => api.post(`/university/enrollments/${eid}/grade`, { letter }),
    onSuccess: () => { toast.success('Grade recorded'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const dropMut = useMutation({ mutationFn: (eid) => api.delete(`/university/enrollments/${eid}`), onSuccess: () => { toast.success('Dropped'); inval(); }, onError: (e) => toast.error(errMsg(e)) });

  const program = (programs || []).find((p) => p.id === activeProgram);
  const semesters = program ? [...Array(program.semesters)].map((_, i) => i + 1) : [];
  const enrollByCourse = {};
  (enrollments || []).forEach((e) => { (enrollByCourse[e.course_id] = enrollByCourse[e.course_id] || []).push(e); });
  const codeOf = (id) => (courses || []).find((c) => c.id === id)?.code || '?';
  const { data: audit } = useQuery({
    queryKey: ['uni-audit', tsid, activeProgram],
    queryFn: () => api.get(`/university/students/${tsid}/degree-audit`, { params: { program_id: activeProgram } }).then((r) => r.data),
    enabled: !!tsid && tsid !== 'me' && !!activeProgram && !isStudent,
  });

  return (
    <div data-testid="university-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Programs - semesters - credit courses - GPA transcripts. Simple, not Banner.</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Buildings size={30} className="text-orange-600" weight="duotone" /> University</h1>
        </div>
        {isAdmin && <Button data-testid="new-program-button" onClick={() => setPOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1" /> New program</Button>}
      </div>

      {isStudent ? (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="my-transcript">
          <h3 className="font-display font-semibold mb-2">My transcript</h3>
          {transcript ? (
            <>
              <p className="text-sm mb-3">CGPA: <span className="font-display font-extrabold text-2xl text-orange-700" data-testid="my-cgpa">{transcript.cgpa ?? '-'}</span> <span className="text-slate-400 text-xs">(10-point · {transcript.total_credits} credits)</span>
                <a data-testid="my-transcript-pdf" href={`${API}/university/students/${transcript.student.id}/transcript.pdf`} className="ml-3 text-xs border border-slate-200 rounded px-2 py-1 hover:border-orange-500">Download PDF</a></p>
              {transcript.semesters.map((s) => (
                <div key={s.semester} className="mb-3">
                  <p className="font-mono-label text-[10px] uppercase text-slate-400">Semester {s.semester} · SGPA {s.sgpa ?? '-'}</p>
                  {s.courses.map((c) => <p key={c.id} className="text-sm">{c.course_code} {c.course_title} · {c.credits}cr · <b>{c.letter || 'in progress'}</b></p>)}
                </div>
              ))}
            </>
          ) : <p className="text-sm text-slate-400">No enrollments yet - your registrar will enroll you into courses.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="programs-list">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 px-3 pt-3 pb-1">Programs</p>
            {(programs || []).map((p) => (
              <button key={p.id} data-testid={`program-item-${p.id}`} onClick={() => setActiveProgram(p.id)}
                className={`w-full text-left px-3 py-2.5 border-t border-slate-100 hover:bg-orange-50/50 flex items-center gap-2 ${activeProgram === p.id ? 'bg-orange-50 text-orange-800 font-semibold' : ''}`}>
                <span className="flex-1 text-sm truncate">{p.degree} {p.name}</span>
                <span className="text-[10px] text-slate-400">{p.semesters} sems</span>
                <CaretRight size={12} />
              </button>
            ))}
            {(programs || []).length === 0 && <p className="p-4 text-sm text-slate-400" data-testid="programs-empty">No programs yet.</p>}
          </div>

          <div className="space-y-4">
            {program ? (
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="program-detail">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <h3 className="font-display font-bold text-lg">{program.degree} {program.name}</h3>
                  <span className="text-xs text-slate-400">{program.department} · {program.duration_years} years</span>
                  {isAdmin && (
                    <span className="ml-auto flex gap-2 items-center">
                      <Button data-testid="add-course-button" size="sm" variant="outline" onClick={() => setCOpen(true)}><Plus size={14} className="mr-1" /> Course</Button>
                      <Button data-testid="program-settings-button" size="sm" variant="outline" onClick={() => { setSform({ reg_open: program.reg_open || '', reg_close: program.reg_close || '', max_credits: program.max_credits || 0 }); setSettingsOpen(true); }}>Rules</Button>
                      <Button data-testid="advance-semester-button" size="sm" variant="outline" className="text-orange-700 border-orange-200 hover:bg-orange-50" disabled={advanceMut.isPending}
                        onClick={() => { if (window.confirm('Advance all fully-graded students to the next semester and auto-enroll them in its courses (prerequisites respected)?')) advanceMut.mutate(); }}>
                        {advanceMut.isPending ? 'Advancing...' : 'Advance semester'}
                      </Button>
                      <button data-testid={`program-delete-${program.id}`} onClick={() => { if (window.confirm('Delete this program?')) pDel.mutate(program.id); }} className="text-xs text-rose-600 underline">Delete</button>
                    </span>
                  )}
                </div>
                {(program.reg_open || program.reg_close || program.max_credits > 0) && (
                  <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3" data-testid="program-rules-line">
                    {program.reg_open || program.reg_close ? `Registration ${program.reg_open || '...'} - ${program.reg_close || '...'}` : ''}
                    {program.max_credits > 0 ? ` · max ${program.max_credits} credits/semester` : ''}
                  </p>
                )}
                {semesters.map((sem) => {
                  const list = (courses || []).filter((c) => c.semester === sem);
                  if (!list.length) return null;
                  return (
                    <div key={sem} className="mb-4">
                      <p className="font-mono-label text-[10px] uppercase text-orange-600 mb-1">Semester {sem}</p>
                      {list.map((c) => (
                        <div key={c.id} className="border border-slate-100 rounded-lg p-3 mb-2" data-testid={`course-row-${c.id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono-label text-xs bg-slate-100 rounded px-1.5 py-0.5">{c.code}</span>
                            <span className="font-medium text-sm">{c.title}</span>
                            <span className="text-xs text-slate-400">{c.credits} credits{c.faculty ? ` · ${c.faculty}` : ''}</span>
                            {(c.prerequisites || []).length > 0 && (
                              <span className="font-mono-label text-[9px] uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`course-prereq-${c.id}`}>
                                prereq: {c.prerequisites.map(codeOf).join(', ')}
                              </span>
                            )}
                            {isAdmin && (
                              <span className="ml-auto flex gap-1.5 items-center">
                                <select data-testid={`enroll-select-${c.id}`} value="" onChange={(e) => e.target.value && enrollMut.mutate({ student_id: e.target.value, course_id: c.id })}
                                  className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-500">
                                  <option value="">Enroll student...</option>
                                  {(students || []).filter((s) => s.status === 'active').map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                                <button data-testid={`course-delete-${c.id}`} onClick={() => { if (window.confirm('Delete course?')) cDel.mutate(c.id); }} className="text-xs text-rose-600">×</button>
                              </span>
                            )}
                          </div>
                          {(enrollByCourse[c.id] || []).length > 0 && (
                            <div className="mt-2 space-y-1">
                              {enrollByCourse[c.id].map((e) => (
                                <div key={e.id} className="flex items-center gap-2 text-xs" data-testid={`enrollment-row-${e.id}`}>
                                  <span className="flex-1 truncate">{e.student_name}</span>
                                  {e.letter ? <span className="font-bold text-green-700">{e.letter} ({e.points} pts)</span> : (
                                    <>
                                      <select data-testid={`grade-select-${e.id}`} value="" onChange={(ev) => ev.target.value && gradeMut.mutate({ eid: e.id, letter: ev.target.value })}
                                        className="border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-500">
                                        <option value="">Grade...</option>
                                        {LETTERS.map((l) => <option key={l} value={l}>{l}</option>)}
                                      </select>
                                      <button data-testid={`enrollment-drop-${e.id}`} onClick={() => dropMut.mutate(e.id)} className="text-rose-500">drop</button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {(courses || []).length === 0 && <p className="text-sm text-slate-400" data-testid="courses-empty">No courses yet - add the first one.</p>}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400">Select or create a program - courses are organised per semester with credits, and GPA is computed automatically.</div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="transcript-panel">
              <h3 className="font-display font-semibold mb-2">Transcript lookup</h3>
              <div className="flex gap-2 items-center flex-wrap">
                <select data-testid="transcript-student-select" value={transcriptFor} onChange={(e) => setTranscriptFor(e.target.value)}
                  className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white w-64">
                  <option value="">Select student...</option>
                  {(students || []).map((s) => <option key={s.id} value={s.id}>{`${s.name} - ${s.admission_no}`}</option>)}
                </select>
                {transcript && <span className="text-sm">CGPA: <b className="text-orange-700" data-testid="transcript-cgpa">{transcript.cgpa ?? 'in progress'}</b> · {transcript.total_credits} credits</span>}
                {transcript && <a data-testid="transcript-pdf-link" href={`${API}/university/students/${transcriptFor}/transcript.pdf`} className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-orange-500">Transcript PDF</a>}
              </div>
              {transcript && transcript.semesters.map((s) => (
                <p key={s.semester} className="text-xs text-slate-500 mt-1.5">Sem {s.semester}: SGPA {s.sgpa ?? '-'} - {s.courses.map((c) => `${c.course_code} ${c.letter || '...'}`).join(', ')}</p>
              ))}
              {audit && (
                <div className="mt-4 border-t border-slate-100 pt-3" data-testid="degree-audit-panel">
                  <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Degree audit - {audit.program.degree} {audit.program.name}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-600 rounded-full transition-all" style={{ width: `${Math.min(100, audit.percent_complete)}%` }} />
                    </div>
                    <span className="text-sm font-bold" data-testid="degree-audit-pct">{audit.percent_complete}%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{audit.earned_credits}/{audit.required_credits} credits earned · {audit.in_progress_credits} in progress{audit.failed_credits ? ` · ${audit.failed_credits} failed` : ''}</p>
                  {audit.remaining.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">Remaining: {audit.remaining.map((r) => `${r.code}${r.status === 'failed' ? ' (retake)' : ''}`).join(', ')}</p>
                  )}
                  {audit.percent_complete >= 100 && (
                    <a data-testid="graduation-certificate-link" href={`${API}/university/students/${tsid}/certificate.pdf?program_id=${activeProgram}`}
                      className="inline-block mt-2 text-xs bg-orange-600 text-white rounded px-3 py-1.5 hover:bg-orange-500">🎓 Degree completion certificate PDF</a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent data-testid="program-create-dialog">
          <DialogHeader><DialogTitle className="font-display">New program</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="program-name-input" placeholder="Program name (e.g. Computer Science)" value={pform.name} onChange={(e) => setPform({ ...pform, name: e.target.value })} />
            <div className="flex gap-2">
              <Input data-testid="program-degree-input" placeholder="Degree (B.Sc / B.Tech / MBA)" value={pform.degree} onChange={(e) => setPform({ ...pform, degree: e.target.value })} />
              <Input data-testid="program-duration-input" type="number" className="w-28" placeholder="Years" value={pform.duration_years} onChange={(e) => setPform({ ...pform, duration_years: e.target.value })} />
            </div>
            <Input data-testid="program-dept-input" placeholder="Department" value={pform.department} onChange={(e) => setPform({ ...pform, department: e.target.value })} />
            <Button data-testid="program-create-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!pform.name || pMut.isPending} onClick={() => pMut.mutate()}>Create program</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent data-testid="course-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Add course - {program?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="course-code-input" className="w-32" placeholder="Code (CS101)" value={cform.code} onChange={(e) => setCform({ ...cform, code: e.target.value })} />
              <Input data-testid="course-title-input" placeholder="Course title" value={cform.title} onChange={(e) => setCform({ ...cform, title: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <select data-testid="course-semester-select" value={cform.semester} onChange={(e) => setCform({ ...cform, semester: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {semesters.map((s) => <option key={s} value={s}>{`Semester ${s}`}</option>)}
              </select>
              <Input data-testid="course-credits-input" type="number" className="w-24" placeholder="Credits" value={cform.credits} onChange={(e) => setCform({ ...cform, credits: e.target.value })} />
              <Input data-testid="course-faculty-input" placeholder="Faculty (optional)" value={cform.faculty} onChange={(e) => setCform({ ...cform, faculty: e.target.value })} />
            </div>
            {(courses || []).filter((c) => c.semester < (parseInt(cform.semester, 10) || 1)).length > 0 && (
              <div data-testid="course-prereq-picker">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Prerequisites (must be passed before enrolling)</p>
                <div className="flex flex-wrap gap-1.5">
                  {(courses || []).filter((c) => c.semester < (parseInt(cform.semester, 10) || 1)).map((c) => {
                    const on = cform.prerequisites.includes(c.id);
                    return (
                      <button key={c.id} data-testid={`prereq-toggle-${c.id}`} onClick={() => setCform({ ...cform, prerequisites: on ? cform.prerequisites.filter((x) => x !== c.id) : [...cform.prerequisites, c.id] })}
                        className={`text-xs rounded px-2 py-1 border transition-colors ${on ? 'bg-orange-600 text-white border-orange-600' : 'bg-white border-slate-200 hover:border-orange-500'}`}>
                        {c.code}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button data-testid="course-create-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!cform.code || !cform.title || cMut.isPending} onClick={() => cMut.mutate()}>Add course</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="program-settings-dialog">
          <DialogHeader><DialogTitle className="font-display">Registration rules - {program?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Registration opens</p>
                <Input data-testid="reg-open-input" type="date" value={sform.reg_open} onChange={(e) => setSform({ ...sform, reg_open: e.target.value })} />
              </div>
              <div className="flex-1">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Registration closes</p>
                <Input data-testid="reg-close-input" type="date" value={sform.reg_close} onChange={(e) => setSform({ ...sform, reg_close: e.target.value })} />
              </div>
            </div>
            <div>
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Max credits per semester (0 = no limit)</p>
              <Input data-testid="max-credits-input" type="number" className="w-32" value={sform.max_credits} onChange={(e) => setSform({ ...sform, max_credits: e.target.value })} />
            </div>
            <p className="text-[11px] text-slate-400">Enrollments outside the window, missing prerequisites or over the credit limit are blocked - admins can override with an audited reason.</p>
            <Button data-testid="program-settings-save" className="w-full bg-slate-900 hover:bg-slate-800" disabled={settingsMut.isPending} onClick={() => settingsMut.mutate()}>Save rules</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
